import { useEffect, useRef } from "react";
import {
  mirrorWaveTodoItemsToCloud,
  WaveBlockKind,
  type WaveBlock,
} from "@vida/wave-core";
import { createWaveApiConfig, trackActivity } from "./waveSession";

const DEBOUNCE_MS = 1500;

/** Debounced mirror of TODO_LIST items to per-user cloud Todo rows. */
export function useWaveTodoMirror(
  waveId: string,
  spaceId: string,
  blocks: WaveBlock[],
  enabled: boolean,
): void {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastSigRef = useRef("");

  useEffect(() => {
    if (!enabled || !waveId || !spaceId) return;

    const todoBlocks = blocks.filter((b) => b.kind === WaveBlockKind.TodoList);
    if (todoBlocks.length === 0) return;

    const sig = JSON.stringify(
      todoBlocks.map((b) => ({
        id: b.id,
        wi: b.payload.widgetIndex,
        items: (b.payload.todoItems ?? []).map((i) => ({
          id: i.id,
          t: i.title,
          d: i.done,
        })),
      })),
    );
    if (sig === lastSigRef.current) return;

    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      lastSigRef.current = sig;
      void (async () => {
        try {
          const result = await mirrorWaveTodoItemsToCloud(
            createWaveApiConfig(),
            blocks,
            { waveId, spaceId, projectId: spaceId },
          );
          if (result.synced > 0) {
            trackActivity("Todo Wave", `${result.synced} todo sincronizzati con Drift`);
          }
          if (result.errors.length > 0) {
            trackActivity("Todo Wave", result.errors[0] ?? "Errore sync");
          }
        } catch (e) {
          trackActivity(
            "Todo Wave",
            e instanceof Error ? e.message : "Sync todo fallita",
          );
        }
      })();
    }, DEBOUNCE_MS);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [blocks, enabled, spaceId, waveId]);
}
